import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SavyIcon from "../../../../../../src/assets/icons/savy.png";
import { useParams } from "react-router-dom";
import axios from "axios";
import { message } from "antd";
import {
  createRefundEstimation,
  REFUND_FLOW_ID,
} from "./refundEstimationApi";

const Welcome = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Start animations after component mounts
    setTimeout(() => setIsAnimating(true), 100);
  }, []);

  const handleButtonClick = (e) => {
    // Add ripple effect
    const button = e.currentTarget;
    const ripple = document.createElement("span");
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;

    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.classList.add("ripple");

    button.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);

    handleNext();
  };

  const handleNext = async () => {
    // Start exit animation
    setIsExiting(true);

    try {
      // Login on every Start click to get fresh token for the flow.
      const loginResponse = await axios.post(
        "https://api.savyapp.dev/api/v1/auth/email/login",
        {
          email: "webflow@savy.app",
          password: "string",
        },
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );

      const { token, refreshToken, user } = loginResponse.data || {};
      if (!token) {
        throw new Error("Login did not return token");
      }
      localStorage.setItem("extarrnalToken", token);
      if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
      if (user?.id) localStorage.setItem("userId", user.id);

      if (id === REFUND_FLOW_ID) {
        // Refund flow: create refund estimation
        const data = await createRefundEstimation();
        const refundEstimationId = data.id || data._id;
        if (refundEstimationId) {
          localStorage.setItem("refundEstimationId", refundEstimationId);
        }
        // Clear old estimation when starting refund flow
        localStorage.removeItem("estimationId");
      } else {
        // Savings / other flow: create old estimation
        const payload = {
          startDate: new Date().toISOString(),
          user: "6899eb6eebdd7e9cc42384da",
          income: null,
          source: "Mobile app",
          status: "In progress",
          ownCar: null,
          mileage: null,
          mileageRate: null,
          foodAndDrink: null,
          daysPerWeek: null,
          reimbursedPerDay: null,
          annualSaving: null,
          trialStarted: null,
          refundYears: 0,
          refund: null,
          onBoardingStatut: null,
          timeSpent: 0,
          mileageType: 0,
          spendPerDay: 0,
          annualExpenses: 0,
        };

        const response = await axios.post(
          "https://api.savyapp.dev/api/v1/estimations/user/me",
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log("Estimation created:", response.data);
        const estimationId = response.data.id;
        localStorage.setItem("estimationId", estimationId);
      }

      // Wait for exit animation before navigating
      setTimeout(() => {
        navigate(`/preview/flow/${id}/question`);
      }, 300);
    } catch (error) {
      console.error("Error creating estimation:", error);
      setIsExiting(false);
      message.error("Failed to start. Please try again.");
    }
  };

  return (
    <div
      className={`flex flex-col h-full bg-white transition-all duration-500 ${
        isExiting ? "opacity-0 scale-95" : "opacity-100 scale-100"
      }`}
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "calc(env(safe-area-inset-bottom) + 20px)",
      }}
    >
      {/* Main Content - Match mobile StartWizard copy and layout */}
      <div className="flex-1 flex flex-col items-center px-4 sm:px-6 md:px-8 min-h-0 pt-8 sm:pt-10">
        <div className="w-full max-w-xl mx-auto flex flex-col text-center h-full">
          {/* 1. Welcome to + Logo pinned at top */}
          <div
            className={`flex flex-col items-center transition-all duration-700 ease-out ${
              isAnimating ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
            style={{ transitionDelay: "100ms" }}
          >
            <p className="text-base sm:text-lg md:text-xl font-bold text-[#6B7280] mb-1">
              Welcome to
            </p>
            <div className="pt-1 pb-2 sm:pb-3">
              <img
                src={SavyIcon}
                alt="Savy"
                className="w-auto h-12 sm:h-14 md:h-16 max-w-[220px] sm:max-w-[250px] md:max-w-[280px]"
              />
            </div>
          </div>

          {/* 2+3 centered in the remaining space */}
          <div className="flex-1 flex flex-col justify-center space-y-4 sm:space-y-6">
            <div
              className={`transition-all duration-700 ease-out ${
                isAnimating ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
              style={{ transitionDelay: "300ms" }}
            >
              <h1 className="text-3xl sm:text-4xl md:text-[2.6rem] font-bold text-gray-900 leading-[1.24] tracking-[-0.01em]">
                We help taxpayers{" "}
                <span className="bg-gradient-to-r from-[#87ACFD] via-[#BE6ED4] to-[#F28789] bg-clip-text text-transparent">
                  get refunds
                </span>
                {" "}for work travel costs.
              </h1>
            </div>

            <div
              className={`transition-all duration-700 ease-out ${
                isAnimating ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
              style={{ transitionDelay: "500ms" }}
            >
              <p className="text-3xl sm:text-4xl md:text-[2.6rem] font-bold text-gray-900 leading-[1.24] tracking-[-0.01em]">
                Give us{" "}
                <span className="bg-gradient-to-r from-[#BE6ED4] to-[#F28789] bg-clip-text text-transparent">
                  30 seconds
                </span>
                {" "}and we'll tell you how much you can get back.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Button - mobile: "Start" */}
      <div className="w-full px-4 sm:px-6 md:px-8 py-4 sm:py-5 bg-white flex-shrink-0">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={handleButtonClick}
            className={`w-full bg-[#002147] text-white font-semibold py-2.5 sm:py-3 rounded-lg hover:bg-[#003366] focus:bg-[#003366] focus:outline-none focus:ring-2 focus:ring-[#002147]/30 shadow-md hover:shadow-lg transform hover:scale-[1.01] active:scale-[0.99] relative overflow-hidden transition-all duration-200 ${
              isAnimating
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
            style={{
              transitionDelay: "1300ms",
            }}
          >
            <span className="relative z-10">Start</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Welcome;
